/* =====================================================================
   tower/driver/src/nudge-rules.js — "time to move" nudges: the RULES, as pure functions (07-ACCEPTANCE F12;
   Parik 2026-10-22: "show notifications on drivers app: time to move to next stop approaching if they still sit
   at previous stop … you can track their location, their submitting stop etc.").

   No DOM, no imports, no clock of its own: every function is given the moment, the fixes and the stops, so the
   same rules run on the phone (nudges.js), on the office's Radar (nudges-office.js, from the telemetry the
   server already relays) and in node (tools/test-nudge-rules.mjs) with simulated GPS tracks.

   Four cues, one at a time, highest first:
     left    moved > leftM away from an UNFINISHED stop the driver was at (≥ minDwellMin)   → Open / Skip
     risk    the next stop's window will be missed at this pace, after finishing (or idle)  → earlier, stronger
     long    at an unfinished stop well past its planned time (longFactor × planned, ≥ longMinMin)
     linger  finished a stop but still within nearM of it for > lingerMin                    → "Time to head to …"
   Rules: never while driving (> drivingMph — a cue waits until the truck stops); each cue once per stop (a snooze
   brings it back once the snooze ends; a dismiss never); nothing before Start or after Done; nothing without a
   fresh, usable GPS fix (position is never guessed); nothing when the company turned nudges off.
   ===================================================================== */

/** The defaults (company-adjustable ones marked *). Distances in metres, times in minutes, speed in mph. */
export const DEFAULTS = Object.freeze({
  on: true,          // * nudges on (default ON)
  lingerMin: 5,      // * finished but still at the stop this long → "time to head to …"
  longFactor: 2,     // * a stop is "long" past this many × its planned time …
  longMinMin: 15,    // * … and never sooner than this
  serviceMin: 10,    // * the planned time at a stop that has none of its own
  nearM: 100,        //   "at the stop"
  leftM: 300,        //   "left the stop"
  drivingMph: 10,    //   faster than this = driving: no cue, it waits
  snoozeMin: 10,     //   Snooze brings a cue back after this
  staleMin: 3,       //   a fix older than this is no fix (quiet — never guess)
  maxAccM: 75,       //   a fix less accurate than this is no fix (arrive / leave are never decided on a rough one)
  minDwellMin: 3,    //   time at a stop before "you left it" can be said (a drive-past is not a visit)
  goneFixes: 2,      //   "left" needs this many fixes in a row beyond leftM …
  goneSec: 30,       //   … spanning at least this long (one multipath jump is not a departure)
  yardM: 150,        //   a stop this close to the route's warehouse never gets "still at" / "you left" (the truck loads there)
  riskAfterMin: 1,   //   window at risk: said this soon after finishing (vs lingerMin)
  idleRiskMin: 3,    //   … or after standing still this long away from any stop
});
const LIMITS = { lingerMin: [1, 60], longFactor: [1, 5], longMinMin: [5, 120], serviceMin: [1, 120] };

/** The company setting (driverApp.nudges, any shape) → a full, clamped config. Unknown/garbage → the default. */
export function normConfig(raw) {
  const c = Object.assign({}, DEFAULTS);
  if (!raw || typeof raw !== 'object') return c;
  if (raw.on === false || raw.on === 'false') c.on = false;
  for (const k of Object.keys(LIMITS)) {
    const v = Number(raw[k]);
    if (raw[k] != null && raw[k] !== '' && isFinite(v)) c[k] = Math.min(LIMITS[k][1], Math.max(LIMITS[k][0], v));
  }
  return c;
}
export const LIMITS_ADJUSTABLE = LIMITS;

/** Great-circle metres between [lat,lng] pairs (null when either is unusable). */
export function metres(a, b) {
  if (!a || !b || !isFinite(a[0]) || !isFinite(a[1]) || !isFinite(b[0]) || !isFinite(b[1])) return null;
  const R = 6371000, rad = Math.PI / 180, dLat = (b[0] - a[0]) * rad, dLng = (b[1] - a[1]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Minutes of driving between two points: straight line × 1.3 for the roads, at 25 mph (a DSD truck's mixed
 *  city/arterial pace). An estimate the cue says as "≈", never an ETA promised to a customer. */
export function driveMin(a, b) {
  const m = metres(a, b); if (m == null) return null;
  return Math.max(1, Math.round((m * 1.3 / 1609.344) / 25 * 60));
}

/** A fresh memory for one phone (or one truck on Radar). */
export function newMem() { return { fixes: [], anchor: null, at: {}, cues: {} }; }

/**
 * Feed one GPS fix. fix = { lat, lng, at (ms), acc? (m), mph? }. stops = [{ key, ll:[lat,lng]|null, … }].
 * Keeps: the last fixes (speed is derived from them when the phone gives none), where the truck has been standing
 * still since (anchor), and per stop when the truck arrived within nearM, was last there, and went past leftM.
 * Returns false when the fix was not usable (too rough, a repeat).
 */
export function track(mem, fix, stops, cfg) {
  cfg = cfg || DEFAULTS;
  if (!fix || !isFinite(fix.lat) || !isFinite(fix.lng) || !isFinite(fix.at)) return false;
  if (fix.acc != null && isFinite(fix.acc) && fix.acc > cfg.maxAccM) return false;
  const last = mem.fixes[mem.fixes.length - 1];
  if (last && fix.at <= last.at) return false;
  const ll = [fix.lat, fix.lng];
  let mph = (fix.mph != null && isFinite(fix.mph)) ? Number(fix.mph) : null;
  if (mph == null && last && fix.at - last.at >= 2000) mph = (metres([last.lat, last.lng], ll) / ((fix.at - last.at) / 1000)) * 2.23694;
  mem.fixes.push({ lat: fix.lat, lng: fix.lng, at: fix.at, mph });
  if (mem.fixes.length > 20) mem.fixes.shift();
  if (!mem.anchor || metres(mem.anchor.ll, ll) > 60) mem.anchor = { ll, since: fix.at };
  (stops || []).forEach((s) => {
    if (!s || !s.ll) return;
    const d = metres(ll, s.ll); if (d == null) return;
    const p = mem.at[s.key];
    if (d <= cfg.nearM) {
      if (!p || p.gone) mem.at[s.key] = { arr: fix.at, last: fix.at, gone: 0, outN: 0, outAt: 0 };   // (back again after a real departure: a new visit)
      else { p.last = fix.at; p.outN = 0; p.outAt = 0; }        // an outlier in between changes nothing
    } else if (d > cfg.leftM && p && !p.gone) {
      // gone only when it holds: goneFixes in a row beyond leftM, spanning goneSec
      if (!p.outN) p.outAt = fix.at;
      p.outN = (p.outN || 0) + 1;
      if (p.outN >= cfg.goneFixes && fix.at - p.outAt >= cfg.goneSec * 1000) p.gone = fix.at;
    }
  });
  return true;
}

/**
 * What is true right now. input = { now, nowMin (company minutes past midnight, for windows), stops: [{ key, name,
 * ll, state: 'open'|'done'|'skipped', doneAt (ms|null), serviceMin (planned minutes|null), winEnd (company minutes|
 * null) }], cur (the next open stop's index, -1 = none), opened (the stop the driver has open on screen, -1 = none),
 * yard ([lat,lng] of the route's warehouse | null) }.
 * "Still at" and "you left" are only ever about the stop being WORKED — the one open on screen, else the next one in
 * the app's order — never another open stop the truck happens to stand near (a plaza, a jam, the yard).
 * → { gps: false } with no usable recent fix, else { gps: true, driving, mph, list: [conditions] }.
 */
export function conditions(input, mem, cfg) {
  cfg = cfg || DEFAULTS;
  const fix = mem.fixes[mem.fixes.length - 1], now = input.now;
  if (!fix || now - fix.at > cfg.staleMin * 60000) return { gps: false, driving: false, list: [] };
  const here = [fix.lat, fix.lng], mph = fix.mph, driving = mph != null && mph > cfg.drivingMph;
  const stops = input.stops || [], list = [];
  const dist = stops.map((s) => (s && s.ll) ? metres(here, s.ll) : null);
  const MIN = 60000;
  const active = (input.opened != null && input.opened >= 0) ? input.opened : input.cur;
  const w = active != null && active >= 0 ? stops[active] : null;
  const byYard = w && w.ll && input.yard ? metres(w.ll, input.yard) : null;
  if (w && w.state === 'open' && !(byYard != null && byYard <= cfg.yardM)) {
    const p = mem.at[w.key], d = dist[active];
    // left: the stop being worked, stood at long enough to be a visit, now well away (and it held)
    if (p && p.gone && p.last - p.arr >= cfg.minDwellMin * MIN && d != null && d > cfg.leftM)
      list.push({ kind: 'left', idx: active, key: w.key, name: w.name });
    // long: at the stop being worked well past its planned time
    else if (p && !p.gone && d != null && d <= cfg.nearM) {
      const planned = (w.serviceMin != null && isFinite(w.serviceMin) && w.serviceMin > 0) ? w.serviceMin : cfg.serviceMin;
      const limit = Math.max(cfg.longMinMin, cfg.longFactor * planned);
      if (now - p.arr >= limit * MIN) list.push({ kind: 'long', idx: active, key: w.key, name: w.name, mins: Math.floor((now - p.arr) / MIN), limit });
    }
  }
  // linger / risk: about the NEXT stop, from where the truck stands
  const next = input.cur >= 0 ? stops[input.cur] : null;
  if (next && !(dist[input.cur] != null && dist[input.cur] <= cfg.nearM)) {
    let from = -1;   // the most recently finished stop the truck is still at
    stops.forEach((s, i) => { if (s.state !== 'open' && s.doneAt && dist[i] != null && dist[i] <= cfg.nearM && (from < 0 || s.doneAt > stops[from].doneAt)) from = i; });
    const atOpen = stops.some((s, i) => s.state === 'open' && dist[i] != null && dist[i] <= cfg.nearM);
    const drive = next.ll ? driveMin(here, next.ll) : null;
    const winEnd = (next.winEnd != null && isFinite(next.winEnd)) ? next.winEnd : null;
    const atRisk = winEnd != null && drive != null && input.nowMin != null && input.nowMin + drive > winEnd;
    const base = { idx: input.cur, nextKey: next.key, next: next.name, drive, winEnd };
    if (from >= 0) {
      const since = now - stops[from].doneAt;
      if (atRisk && since >= cfg.riskAfterMin * MIN) list.push(Object.assign({ kind: 'risk', key: next.key, from, fromKey: stops[from].key }, base));
      else if (since >= cfg.lingerMin * MIN) list.push(Object.assign({ kind: 'linger', key: stops[from].key, from, fromKey: stops[from].key, mins: Math.floor(since / MIN) }, base));
    } else if (atRisk && !atOpen && mem.anchor && now - mem.anchor.since >= cfg.idleRiskMin * MIN && !dist.some((d) => d != null && d <= cfg.nearM)) {
      list.push(Object.assign({ kind: 'risk', key: next.key, from: -1 }, base));
    }
  }
  return { gps: true, driving, mph, list };
}

const ORDER = ['left', 'risk', 'long', 'linger'];
export const cueId = (c) => c.kind + '|' + c.key;

/**
 * Which cue to show now, or null. gate = { on, started, ended }. Records what it shows in mem.cues (once per stop
 * per kind; a snooze sets `until`, a dismiss sets `done`). A cue on screen whose condition has gone (the driver
 * left, finished, …) is retired. While driving nothing is shown — the condition is simply re-checked when the
 * truck stops, so a cue "waits" without a queue that could go stale.
 */
export function pick(res, mem, now, gate, showing) {
  if (!gate || !gate.on || !gate.started || gate.ended || !res || !res.gps) return null;
  const live = new Set(res.list.map(cueId));
  if (showing && !live.has(showing)) { const s = mem.cues[showing]; if (s) s.done = true; showing = null; }
  // moving off answers "time to head to …": a linger / risk cue goes once the truck drives
  if (showing && res.driving && /^(linger|risk)\|/.test(showing)) { const s = mem.cues[showing]; if (s) s.done = true; showing = null; }
  // a cue already up that is still true stays up — also while driving ("still at" / "you left" do not stop being true)
  if (showing) return res.list.find((c) => cueId(c) === showing) || null;
  if (res.driving) return null;   // a NEW cue waits until the truck stops
  const sorted = res.list.slice().sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  for (const c of sorted) {
    const id = cueId(c), s = mem.cues[id];
    if (s && (s.done || !s.until || now < s.until)) continue;
    mem.cues[id] = { shown: now, until: 0, done: false };
    // the stronger cue says what the weaker one would have: a risk cue retires the linger cue for the same stop
    if (c.kind === 'risk' && c.fromKey) mem.cues['linger|' + c.fromKey] = { shown: now, until: 0, done: true };
    return c;
  }
  return null;
}
/** The driver's answers. */
export function snooze(mem, id, now, cfg) { const s = mem.cues[id] || (mem.cues[id] = { shown: now }); s.until = now + (cfg || DEFAULTS).snoozeMin * 60000; s.done = false; }
export function dismiss(mem, id) { const s = mem.cues[id] || (mem.cues[id] = {}); s.done = true; }
/** What survives a reload (per day, per driver — the caller scopes the key): arrivals at stops and what was said. */
export function persist(mem) { return { at: mem.at, cues: mem.cues }; }
export function restore(mem, saved) {
  if (!saved || typeof saved !== 'object') return mem;
  if (saved.at && typeof saved.at === 'object') mem.at = saved.at;
  if (saved.cues && typeof saved.cues === 'object') mem.cues = saved.cues;
  return mem;
}
